import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, CheckCircle2, Loader2, Image as ImageIcon, Sparkles, ZoomIn, ZoomOut, ArrowLeft, Crop } from 'lucide-react';
import Cropper from 'react-easy-crop';
import { getMediaListApi, getUserAvatarHistoryApi, updateUserAvatarApi, uploadMediaApi, customFetch } from '../services/apiService';
import { UserProfile } from '../types';

const AVATAR_MEDIA_FOLDER = 'class/teacher_avatar';

interface AvatarPickerModalProps {
  isOpen: boolean;
  currentPhotoURL: string | null;
  onClose: () => void;
  onAvatarUpdated: (newPhotoURL: string, updatedUser?: UserProfile) => void;
  onShowToast: (type: 'success' | 'error', message: string) => void;
}

// Helper function to create cropped image canvas
const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: { x: number; y: number; width: number; height: number }
): Promise<string> => {
  const image = new Image();
  image.src = imageSrc;
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
  });

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas context unavailable');

  // Output 400x400 square for avatar
  const targetSize = 400;
  canvas.width = targetSize;
  canvas.height = targetSize;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    targetSize,
    targetSize
  );

  return canvas.toDataURL('image/jpeg', 0.8);
};

export const AvatarPickerModal: React.FC<AvatarPickerModalProps> = ({
  isOpen,
  currentPhotoURL,
  onClose,
  onAvatarUpdated,
  onShowToast
}) => {
  const [avatars, setAvatars] = useState<string[]>([]);
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);

  // Crop & Zoom State
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<{ x: number; y: number; width: number; height: number } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Automatically fetch avatar media list when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAvatarHistory();
      setSelectedUrl(currentPhotoURL || null);
      setImageToCrop(null);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
    }
  }, [isOpen, currentPhotoURL]);

  const fetchAvatarHistory = async () => {
    setLoadingHistory(true);
    try {
      // 1. Thử lấy danh sách ảnh từ API: /api/v1/media/list?folder=class/teacher_avatar
      const mediaRes = await getMediaListApi(AVATAR_MEDIA_FOLDER);
      let list: string[] = [];

      if (mediaRes.success && Array.isArray(mediaRes.urls) && mediaRes.urls.length > 0) {
        list = mediaRes.urls;
      } else {
        // 2. Fallback sang getUserAvatarHistoryApi
        const historyRes = await getUserAvatarHistoryApi(AVATAR_MEDIA_FOLDER);
        if (historyRes.success && Array.isArray(historyRes.avatars)) {
          list = historyRes.avatars;
        }
      }

      // Filter duplicates and valid non-empty URLs
      const cleaned = Array.from(new Set(list.filter((u): u is string => Boolean(u && typeof u === 'string' && u.trim().length > 0))));
      setAvatars(cleaned);
    } catch (err) {
      console.error("Lỗi khi tải thư viện ảnh đại diện:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate size <= 3MB
    if (file.size > 3 * 1024 * 1024) {
      onShowToast('error', 'Dung lượng ảnh chọn không được vượt quá 3MB. Vui lòng chọn ảnh nhỏ hơn!');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setImageToCrop(reader.result as string);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
    };
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleApplyCroppedAvatar = async () => {
    if (!imageToCrop || !croppedAreaPixels) return;

    setUploading(true);
    try {
      const croppedBase64 = await getCroppedImg(imageToCrop, croppedAreaPixels);

      // Upload qua API Media Service /api/v1/media/upload?folder=class/teacher_avatar
      let uploadedMediaUrl: string | undefined = undefined;
      try {
        const fetchRes = await customFetch(croppedBase64);
        const blob = await fetchRes.blob();
        const uploadRes = await uploadMediaApi(blob, AVATAR_MEDIA_FOLDER, `avatar_${Date.now()}.jpg`);
        if (uploadRes.success && uploadRes.url) {
          uploadedMediaUrl = uploadRes.url;
        }
      } catch (mediaErr) {
        console.warn("Lỗi upload media service, fallback sang base64:", mediaErr);
      }

      const finalAvatarUrl = uploadedMediaUrl || croppedBase64;
      const res = await updateUserAvatarApi({
        avatar: finalAvatarUrl,
        photoURL: finalAvatarUrl,
        imageBase64: croppedBase64,
        folder: AVATAR_MEDIA_FOLDER
      });

      if (res.success) {
        onShowToast('success', 'Cập nhật ảnh đại diện thành công!');
        const finalPhotoURL = res.photoURL || finalAvatarUrl;
        onAvatarUpdated(finalPhotoURL, res.user);
        setImageToCrop(null);
        // Refresh danh sách media avatar
        fetchAvatarHistory();
        onClose();
      } else {
        onShowToast('error', res.message || 'Tải ảnh đại diện thất bại.');
      }
    } catch (err: any) {
      onShowToast('error', err.message || 'Lỗi xử lý nén/cắt ảnh.');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmSelectExisting = async () => {
    if (!selectedUrl) return;

    setUploading(true);
    try {
      const res = await updateUserAvatarApi({ 
        avatar: selectedUrl,
        photoURL: selectedUrl,
        folder: AVATAR_MEDIA_FOLDER 
      });

      if (res.success) {
        onShowToast('success', 'Cập nhật ảnh đại diện thành công!');
        const finalPhoto = res.photoURL || selectedUrl;
        onAvatarUpdated(finalPhoto, res.user);
        onClose();
      } else {
        onShowToast('error', res.message || 'Cập nhật ảnh đại diện thất bại.');
      }
    } catch (err: any) {
      onShowToast('error', err.message || 'Lỗi khi kết nối máy chủ.');
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl border border-slate-100 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center space-x-2.5">
            {imageToCrop ? (
              <button
                type="button"
                onClick={() => setImageToCrop(null)}
                disabled={uploading}
                className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-200/60 rounded-lg transition-colors mr-1"
                title="Quay lại"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            ) : (
              <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
                <ImageIcon className="w-5 h-5" />
              </div>
            )}
            <h3 className="text-lg font-bold text-slate-800">
              {imageToCrop ? 'Cắt & Điều chỉnh ảnh đại diện' : 'Đổi ảnh đại diện'}
            </h3>
          </div>
          <button
            onClick={() => {
              setImageToCrop(null);
              onClose();
            }}
            disabled={uploading}
            className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        {imageToCrop ? (
          /* CROP & ZOOM INTERACTIVE VIEW */
          <div className="p-6 space-y-5 flex flex-col items-center">
            <p className="text-sm font-medium text-slate-600 text-center">
              Kéo thả để di chuyển vị trí và dùng thanh trượt bên dưới để phóng to / thu nhỏ ảnh.
            </p>

            {/* Cropper Container */}
            <div className="relative w-full h-72 rounded-2xl overflow-hidden bg-slate-900 shadow-inner">
              <Cropper
                image={imageToCrop}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
              />
            </div>

            {/* Zoom Slider Control */}
            <div className="w-full space-y-1.5 bg-slate-50 p-3.5 rounded-2xl border border-slate-200/80">
              <div className="flex items-center justify-between text-xs font-semibold text-slate-600 mb-1">
                <span className="flex items-center space-x-1">
                  <Crop className="w-3.5 h-3.5 text-indigo-600" />
                  <span>Thu phóng (Zoom)</span>
                </span>
                <span>{Math.round(zoom * 100)}%</span>
              </div>
              <div className="flex items-center space-x-3">
                <ZoomOut className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.02}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="w-full accent-indigo-600 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                />
                <ZoomIn className="w-4 h-4 text-slate-400 flex-shrink-0" />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="w-full flex items-center justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={() => setImageToCrop(null)}
                disabled={uploading}
                className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
              >
                Hủy chọn ảnh
              </button>
              <button
                type="button"
                disabled={uploading}
                onClick={handleApplyCroppedAvatar}
                className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white text-sm font-semibold rounded-xl shadow-md shadow-indigo-500/20 flex items-center space-x-2 transition-all disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Đang xử lý & Lưu...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Áp dụng & Tải lên</span>
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          /* SELECTION & HISTORY VIEW */
          <div className="p-6 overflow-y-auto space-y-6">
            {/* PHẦN A: NÚT TẢI ẢNH MỚI TỪ THIẾT BỊ */}
            <div className="space-y-2">
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                className="hidden"
                onChange={handleFileSelect}
              />
              <button
                type="button"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-3 px-4 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.99] text-white font-semibold rounded-xl shadow-md shadow-indigo-500/20 flex items-center justify-center space-x-2 transition-all disabled:opacity-60"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Đang xử lý ảnh...</span>
                  </>
                ) : (
                  <>
                    <span className="text-lg">➕</span>
                    <span>Tải ảnh mới từ máy tính / điện thoại</span>
                  </>
                )}
              </button>
              <p className="text-xs text-center text-slate-400">
                Hỗ trợ JPG, PNG (Dung lượng tối đa 3MB, hỗ trợ cắt & phóng to ảnh)
              </p>
            </div>

            <div className="relative flex py-1 items-center">
              <div className="flex-grow border-t border-slate-200"></div>
              <span className="flex-shrink mx-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                Hoặc chọn từ thư viện
              </span>
              <div className="flex-grow border-t border-slate-200"></div>
            </div>

            {/* PHẦN B: THƯ VIỆN ẢNH ĐÃ TẢI TRƯỚC ĐÂY (AVATAR HISTORY GRID) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-slate-700">
                  Thư viện ảnh đã tải trước đây
                </label>
                {loadingHistory && (
                  <div className="flex items-center text-xs text-indigo-600">
                    <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                    <span>Đang tải...</span>
                  </div>
                )}
              </div>

              {loadingHistory ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 py-4">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className="aspect-square bg-slate-100 rounded-2xl animate-pulse" />
                  ))}
                </div>
              ) : avatars.length === 0 ? (
                <div className="text-center py-8 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                  <ImageIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">Chưa có lịch sử ảnh đại diện nào.</p>
                  <p className="text-xs text-slate-400 mt-1">Hãy tải ảnh mới từ thiết bị của bạn ở trên.</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-60 overflow-y-auto pr-1">
                  {avatars.map((url, idx) => {
                    const isCurrent = currentPhotoURL === url;
                    const isSelected = selectedUrl === url;

                    return (
                      <div
                        key={idx}
                        onClick={() => setSelectedUrl(url)}
                        className={`relative aspect-square rounded-2xl overflow-hidden cursor-pointer border-2 transition-all group ${
                          isSelected
                            ? 'border-indigo-600 ring-4 ring-indigo-500/20 shadow-md scale-[1.02]'
                            : 'border-slate-200 hover:border-indigo-300 hover:shadow-sm'
                        }`}
                      >
                        <img
                          src={url}
                          alt={`Avatar history ${idx}`}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />

                        {/* Current active avatar badge */}
                        {isCurrent && (
                          <div className="absolute top-1.5 left-1.5 bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center shadow-sm">
                            <CheckCircle2 className="w-3 h-3 mr-0.5" />
                            <span>Đang dùng</span>
                          </div>
                        )}

                        {/* Selected indicator check mark if not current */}
                        {!isCurrent && isSelected && (
                          <div className="absolute inset-0 bg-indigo-900/20 flex items-center justify-center">
                            <div className="bg-indigo-600 text-white p-1.5 rounded-full shadow-md">
                              <CheckCircle2 className="w-4 h-4" />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer actions when not cropping */}
        {!imageToCrop && (
          <div className="px-6 py-4 bg-slate-50/80 border-t border-slate-100 flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={uploading}
              className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-200/60 rounded-xl transition-colors"
            >
              Hủy
            </button>
            <button
              type="button"
              disabled={uploading || !selectedUrl || selectedUrl === currentPhotoURL}
              onClick={handleConfirmSelectExisting}
              className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white text-sm font-semibold rounded-xl shadow-md shadow-indigo-500/20 flex items-center space-x-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Đang lưu...</span>
                </>
              ) : (
                <span>Xác nhận chọn</span>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
